import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { isNetworkError } from '@/utils/network';
import { useSession } from 'next-auth/react';
import { useUser } from '@/contexts/UserContext';
import type { WorkspaceDto } from '@/types/workspace';
import { createLogger } from '@/utils/logger';

const logger = createLogger('Account Context');

/**
 * AccountContext — the Phase 3 workspace switcher state, nested inside
 * UserProvider. Owns the list of workspaces, the active workspace, and the
 * mutations (create/rename/archive/set-default). Every data hook reads
 * `activeWorkspaceId` from here and includes it in its payload AND its query key
 * so switching workspaces invalidates per-workspace caches (the #1 cache-poison
 * risk, plan §7).
 *
 * Gated by NEXT_PUBLIC_WORKSPACES_ENABLED. While disabled (the default until the
 * backfill is verified complete — Stage C), no workspaces are fetched and
 * `activeWorkspaceId` is undefined, so the app behaves exactly as today: queries
 * send no workspace_id and the backend stays user-scoped.
 */

export const WORKSPACES_ENABLED = process.env.NEXT_PUBLIC_WORKSPACES_ENABLED === 'true';

// Cookie mirroring the active workspace so SSR (getServerSideProps) can scope the
// initial documents/transactions fetch. Read server-side via req.cookies.
export const WS_COOKIE = 'rai_ws';

interface AccountContextType {
  workspaces: WorkspaceDto[];
  activeWorkspaceId: string | undefined;
  activeWorkspace: WorkspaceDto | undefined;
  loading: boolean;
  enabled: boolean;
  /** True once scoped queries may safely run: workspaces are disabled, an active
   *  workspace is known, or the fetch has settled (so the UI falls back to
   *  user-scoped data instead of hanging on a loading skeleton forever). */
  ready: boolean;
  /** True when a member-only account has been removed from its last workspace (or
   *  the owner deleted their account) and now has no workspace to work in. The app
   *  shell shows a "no access" state instead of empty, broken data views (§9.2). */
  noWorkspaceAccess: boolean;
  setActiveWorkspace: (id: string) => void;
  /** Re-fetch the workspace list. When `preferredId` is supplied and present in
   *  the fetched list, it becomes the active workspace (used after accepting an
   *  invite to land directly in the joined workspace, instead of the default). */
  refreshWorkspaces: (preferredId?: string) => Promise<void>;
  createWorkspace: (input: { name: string; type?: string }) => Promise<WorkspaceDto>;
  renameWorkspace: (id: string, name: string) => Promise<void>;
  /** Set/clear the smart-email-routing description for a workspace. */
  setWorkspaceRoutingHint: (id: string, hint: string) => Promise<void>;
  /** Hard-delete a workspace and ALL its data (owner-only, irreversible). */
  deleteWorkspace: (id: string) => Promise<void>;
}

const noop = () => undefined;
const AccountContext = createContext<AccountContextType>({
  workspaces: [],
  activeWorkspaceId: undefined,
  activeWorkspace: undefined,
  loading: false,
  enabled: false,
  ready: true,
  noWorkspaceAccess: false,
  setActiveWorkspace: noop,
  refreshWorkspaces: () => Promise.resolve(),
  createWorkspace: () => Promise.reject(new Error('workspaces disabled')),
  renameWorkspace: () => Promise.resolve(),
  setWorkspaceRoutingHint: () => Promise.resolve(),
  deleteWorkspace: () => Promise.resolve(),
});

export const useAccount = () => useContext(AccountContext);

/** Convenience hook for data hooks: the active workspace id (or undefined). */
export const useActiveWorkspaceId = () => useContext(AccountContext).activeWorkspaceId;

/** Convenience hook for data hooks: whether workspace-scoped queries may run.
 *  Guards against the page hanging when no active workspace is ever resolved. */
export const useWorkspaceReady = () => useContext(AccountContext).ready;

/** The currency amounts should be DISPLAYED in: the active workspace's currency.
 *  Currency conversion targets the workspace currency (Phase 3 backend), so
 *  comparing converted_currency against the user-level default breaks in any
 *  workspace whose currency differs from it (amounts silently fall back to
 *  their original currency). Falls back to the user default when no workspace
 *  is resolved (workspaces still loading, or feature off). */
export const useDisplayCurrency = (userDefaultCurrency?: string | null): string | undefined => {
  const { activeWorkspace } = useContext(AccountContext);
  return activeWorkspace?.currency || userDefaultCurrency || undefined;
};

const storageKey = (userId: string) => `receiptsai.activeWorkspaceId.${userId}`;

/** Persist a workspace choice from outside the provider. Used by the invite
 *  accept page, where the session may be seconds old: the provider hasn't
 *  hydrated a userId yet, so refreshWorkspaces() would silently no-op and the
 *  just-joined workspace id would be lost. Writing localStorage + the SSR cookie
 *  directly lets the next full page load land in the chosen workspace. */
export const persistWorkspaceChoice = (userId: string | undefined, workspaceId: string) => {
  if (typeof window === 'undefined') return;
  if (userId) window.localStorage.setItem(storageKey(userId), workspaceId);
  document.cookie = `${WS_COOKIE}=${workspaceId}; path=/; max-age=31536000; samesite=lax`;
};

export const AccountProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  // The session id is available the moment NextAuth authenticates — well before
  // UserContext finishes its get_user fetch — so fall back to it. Without the
  // fallback, a freshly-invited user's first load raced UserContext and the
  // workspace fetch never fired (empty workspace until manual refresh).
  const { data: session } = useSession();
  // get_user returns the user keyed by `_id` (the UserDto `id` field is aspirational);
  // accept either so the workspace fetch actually fires.
  const userId =
    user?.id ??
    (user as { _id?: string } | null)?._id ??
    (session?.user as { id?: string } | undefined)?.id;

  const [workspaces, setWorkspaces] = useState<WorkspaceDto[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  // Flips true after the first fetch attempt settles (success OR failure), so
  // scoped queries can fall back to user scope instead of hanging if the fetch
  // returns no workspaces or errors out.
  const [loaded, setLoaded] = useState(false);
  // Flips true only after a *successful* fetch. The "no workspace access" state
  // must never fire off a failed/unauthenticated fetch that settled empty — that
  // shows a false "you have no access" to users who do have workspaces.
  const [loadedOk, setLoadedOk] = useState(false);

  const persistActive = useCallback((id: string | undefined) => {
    setActiveWorkspaceId(id);
    if (typeof window === 'undefined' || !userId) return;
    // localStorage + cookie mirror so getServerSideProps can scope the first
    // paint to the active workspace (avoids the SSR-renders-Personal-then-swaps
    // flash). Backend still scopes by the authenticated user, so a stale cookie
    // is safe.
    if (id) {
      persistWorkspaceChoice(userId, id);
    } else {
      window.localStorage.removeItem(storageKey(userId));
      document.cookie = `${WS_COOKIE}=; path=/; max-age=0; samesite=lax`;
    }
  }, [userId]);

  const pickActive = useCallback((list: WorkspaceDto[]): string | undefined => {
    if (list.length === 0) return undefined;
    const selectable = list.filter((w) => w.status !== 'archived');
    let stored: string | null = null;
    if (typeof window !== 'undefined' && userId) {
      stored = window.localStorage.getItem(storageKey(userId));
    }
    // Honor a stored choice only if it still exists and is selectable.
    if (stored && selectable.some((w) => w.id === stored)) return stored;
    const def = selectable.find((w) => w.is_default) ?? selectable[0];
    return def?.id;
  }, [userId]);

  const refreshWorkspaces = useCallback(async (preferredId?: string) => {
    if (!WORKSPACES_ENABLED || !userId) return;
    try {
      setLoading(true);
      const res = await axios.get<{ workspaces: WorkspaceDto[] }>('/api/workspaces');
      const list = res.data.workspaces ?? [];
      setWorkspaces(list);
      setLoadedOk(true);
      // Prefer an explicitly-requested workspace (e.g. just-joined) when present in
      // the freshly-fetched list; otherwise fall back to stored/default selection.
      if (preferredId && list.some((w) => w.id === preferredId)) persistActive(preferredId);
      else persistActive(pickActive(list));
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401 || status === 403) {
        // Session expired mid-page (e.g. an old tab) — the auth layer redirects
        // to login; nothing to report.
        logger.debug('Workspace fetch unauthorized; session likely expired');
      } else if (isNetworkError(err)) {
        logger.debug('Workspace fetch failed: network error', { error: (err as Error).message });
      } else {
        logger.error('Failed to load workspaces', { error: (err as Error).message });
      }
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [userId, persistActive, pickActive]);

  useEffect(() => {
    if (WORKSPACES_ENABLED && userId) {
      void refreshWorkspaces();
    }
  }, [userId, refreshWorkspaces]);

  const setActiveWorkspace = useCallback((id: string) => {
    if (workspaces.some((w) => w.id === id)) persistActive(id);
  }, [workspaces, persistActive]);

  const createWorkspace = useCallback(async (input: { name: string; type?: string }) => {
    const res = await axios.post<{ workspace: WorkspaceDto }>('/api/workspaces', input);
    await refreshWorkspaces();
    persistActive(res.data.workspace.id);
    return res.data.workspace;
  }, [refreshWorkspaces, persistActive]);

  const renameWorkspace = useCallback(async (id: string, name: string) => {
    await axios.patch(`/api/workspaces/${id}`, { action: 'rename', name });
    await refreshWorkspaces();
  }, [refreshWorkspaces]);

  const setWorkspaceRoutingHint = useCallback(async (id: string, hint: string) => {
    await axios.patch(`/api/workspaces/${id}`, { action: 'set_routing_hint', hint });
    await refreshWorkspaces();
  }, [refreshWorkspaces]);

  const deleteWorkspace = useCallback(async (id: string) => {
    await axios.delete(`/api/workspaces/${id}`);
    // Refetch re-picks a valid active workspace if the deleted one was active.
    await refreshWorkspaces();
  }, [refreshWorkspaces]);

  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.id === activeWorkspaceId),
    [workspaces, activeWorkspaceId]
  );

  // §15.1/§9.2: a member-only account that has lost every workspace (removed from
  // its last one, or the owner deleted their account) has nothing to work in.
  // Keyed on loadedOk (successful fetch), not loaded: a fetch that errored or ran
  // before auth settled must not flash "no access" at a user who has workspaces.
  const noWorkspaceAccess =
    WORKSPACES_ENABLED && loadedOk && workspaces.length === 0 && !!(user as { member_only?: boolean } | null)?.member_only;

  const value: AccountContextType = {
    workspaces,
    activeWorkspaceId,
    activeWorkspace,
    loading,
    enabled: WORKSPACES_ENABLED,
    ready: !WORKSPACES_ENABLED || !!activeWorkspaceId || loaded,
    noWorkspaceAccess,
    setActiveWorkspace,
    refreshWorkspaces,
    createWorkspace,
    renameWorkspace,
    setWorkspaceRoutingHint,
    deleteWorkspace,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export default AccountContext;
